/**
 * See360 Gallery Component
 * Horizontal thumbnail ribbon + lightbox overlay for vehicle images.
 *
 * Behaviour:
 *  - Thumbnail ribbon below the viewer canvas
 *  - Click thumbnail → swaps image into the viewer area
 *  - Click the swapped image → opens full-screen lightbox
 *  - Arrow keys / swipe for prev/next in lightbox
 *  - Escape to close lightbox
 */

export interface GalleryImage {
  url: string;
  alt?: string;
  width?: number;
  height?: number;
}

export interface GalleryConfig {
  container: HTMLElement;        // The viewer wrapper to attach below
  images: GalleryImage[];
  spinThumbnailUrl?: string;     // URL of a 360° frame to use as the "back to 360°" thumbnail
  thumbWidth?: number;           // Thumbnail width in px (default: 80)
  thumbHeight?: number;          // Thumbnail height in px (default: 60)
  bgColor?: string;              // Ribbon background color (default: '#0a0a0a')
  onImageSelect?: (index: number) => void;  // Called when thumbnail clicked
}

export class Gallery {
  private config: GalleryConfig;
  private ribbon: HTMLDivElement;
  private lightbox: HTMLDivElement | null = null;
  private viewerOverlay: HTMLDivElement | null = null;
  private currentIndex = -1;     // -1 = no image selected (viewer is showing 360°)
  private galleryStylesInjected = false;

  constructor(config: GalleryConfig) {
    this.config = config;
    this.ribbon = document.createElement('div');
    this.injectGalleryStyles();
    this.buildRibbon();
    this.applyCustomStyles();
    this.bindKeys();
  }

  // ─── Apply per-instance custom styles ───────────────────

  private applyCustomStyles(): void {
    const tw = this.config.thumbWidth || 80;
    const th = this.config.thumbHeight || 60;
    const bg = this.config.bgColor || '#0a0a0a';
    const ribbonH = th + 20; // padding (8+8) + thumb height + buffer

    this.ribbon.style.setProperty('--s360-thumb-w', `${tw}px`);
    this.ribbon.style.setProperty('--s360-thumb-h', `${th}px`);
    this.ribbon.style.setProperty('--s360-ribbon-bg', bg);
    this.ribbon.style.setProperty('--s360-ribbon-max-h', `${ribbonH}px`);
    this.ribbon.style.setProperty('--s360-ribbon-min-h', `${ribbonH - 4}px`);
  }

  // ─── Inject scoped CSS ─────────────────────────────────

  private injectGalleryStyles(): void {
    if (this.galleryStylesInjected) return;
    this.galleryStylesInjected = true;

    const style = document.createElement('style');
    style.textContent = `
      .s360-gallery-ribbon {
        display: flex !important;
        flex-direction: row !important;
        flex-wrap: nowrap !important;
        gap: 6px !important;
        padding: 8px 10px !important;
        overflow-x: auto !important;
        overflow-y: hidden !important;
        background: var(--s360-ribbon-bg, #0a0a0a) !important;
        border-top: 1px solid rgba(255,255,255,0.08) !important;
        scrollbar-width: thin;
        scrollbar-color: rgba(255,255,255,0.15) transparent;
        -webkit-overflow-scrolling: touch;
        box-sizing: border-box !important;
        margin: 0 !important;
        max-height: var(--s360-ribbon-max-h, 80px) !important;
        min-height: var(--s360-ribbon-min-h, 76px) !important;
      }
      .s360-gallery-ribbon::-webkit-scrollbar { height: 4px; }
      .s360-gallery-ribbon::-webkit-scrollbar-track { background: transparent; }
      .s360-gallery-ribbon::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 2px; }
      .s360-gallery-thumb {
        flex-shrink: 0 !important;
        width: var(--s360-thumb-w, 80px) !important;
        height: var(--s360-thumb-h, 60px) !important;
        min-width: var(--s360-thumb-w, 80px) !important;
        min-height: var(--s360-thumb-h, 60px) !important;
        max-width: var(--s360-thumb-w, 80px) !important;
        max-height: var(--s360-thumb-h, 60px) !important;
        border-radius: 6px !important;
        overflow: hidden !important;
        cursor: pointer !important;
        border: 2px solid transparent !important;
        transition: border-color 0.2s, transform 0.15s;
        position: relative !important;
        display: block !important;
        box-sizing: border-box !important;
        padding: 0 !important;
        margin: 0 !important;
        float: none !important;
      }
      .s360-gallery-thumb:hover {
        transform: scale(1.05);
        border-color: rgba(0,212,255,0.4) !important;
      }
      .s360-gallery-thumb.active {
        border-color: #00d4ff !important;
        box-shadow: 0 0 12px rgba(0,212,255,0.3);
      }
      .s360-gallery-thumb img {
        width: 100% !important;
        height: 100% !important;
        max-width: 100% !important;
        max-height: 100% !important;
        min-width: 0 !important;
        min-height: 0 !important;
        object-fit: cover !important;
        display: block !important;
        border: none !important;
        padding: 0 !important;
        margin: 0 !important;
        border-radius: 0 !important;
        box-shadow: none !important;
        position: static !important;
      }
      .s360-gallery-back-btn {
        flex-shrink: 0 !important;
        width: var(--s360-thumb-w, 80px) !important;
        height: var(--s360-thumb-h, 60px) !important;
        min-width: var(--s360-thumb-w, 80px) !important;
        max-width: var(--s360-thumb-w, 80px) !important;
        min-height: var(--s360-thumb-h, 60px) !important;
        max-height: var(--s360-thumb-h, 60px) !important;
        border-radius: 6px !important;
        border: 2px solid rgba(0,212,255,0.5) !important;
        background: #111 !important;
        cursor: pointer !important;
        display: block !important;
        position: relative !important;
        overflow: hidden !important;
        transition: all 0.2s;
        padding: 0 !important;
        margin: 0 !important;
        box-sizing: border-box !important;
      }
      .s360-gallery-back-btn:hover {
        border-color: #00d4ff !important;
        transform: scale(1.05);
        box-shadow: 0 0 12px rgba(0,212,255,0.3);
      }
      .s360-gallery-back-btn img {
        width: 100% !important;
        height: 100% !important;
        object-fit: cover !important;
        display: block !important;
        border: none !important;
        padding: 0 !important;
        margin: 0 !important;
        border-radius: 0 !important;
        box-shadow: none !important;
        position: static !important;
      }
      .s360-gallery-360-badge {
        position: absolute !important;
        inset: 0 !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        background: rgba(0,0,0,0.35) !important;
        pointer-events: none !important;
      }
      .s360-gallery-360-badge svg {
        width: 56px !important;
        height: 56px !important;
        opacity: 0.95;
        filter: drop-shadow(0 1px 3px rgba(0,0,0,0.6));
      }
      .s360-gallery-wrapper {
        position: relative !important;
        display: block !important;
        box-sizing: border-box !important;
      }
      .s360-gallery-arrow {
        position: absolute !important;
        top: 0 !important;
        bottom: 0 !important;
        width: 28px !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        background: linear-gradient(90deg, rgba(0,0,0,0.7), transparent) !important;
        border: none !important;
        cursor: pointer !important;
        z-index: 10 !important;
        opacity: 0;
        transition: opacity 0.2s;
        padding: 0 !important;
        margin: 0 !important;
      }
      .s360-gallery-wrapper:hover .s360-gallery-arrow {
        opacity: 1;
      }
      .s360-gallery-arrow--left {
        left: 0 !important;
        border-radius: 0 !important;
      }
      .s360-gallery-arrow--right {
        right: 0 !important;
        border-radius: 0 !important;
        background: linear-gradient(-90deg, rgba(0,0,0,0.7), transparent) !important;
      }
      .s360-gallery-arrow svg {
        width: 16px !important;
        height: 16px !important;
        fill: none !important;
        stroke: white !important;
        stroke-width: 2.5 !important;
        stroke-linecap: round !important;
        stroke-linejoin: round !important;
        filter: drop-shadow(0 1px 2px rgba(0,0,0,0.5));
      }
      .s360-gallery-arrow:hover svg {
        stroke: #00d4ff !important;
      }
      .s360-viewer-overlay {
        position: absolute !important;
        inset: 0 !important;
        z-index: 50 !important;
        background: #111 !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        cursor: zoom-in;
      }
      .s360-viewer-overlay img {
        max-width: 100% !important;
        max-height: 100% !important;
        object-fit: contain !important;
        border: none !important;
        box-shadow: none !important;
        border-radius: 0 !important;
        margin: 0 !important;
        padding: 0 !important;
      }
      .s360-viewer-overlay-close {
        position: absolute;
        top: 10px;
        right: 10px;
        width: 32px;
        height: 32px;
        border-radius: 50%;
        border: none;
        background: rgba(0,0,0,0.6);
        color: #fff;
        font-size: 16px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 51;
        transition: background 0.2s;
      }
      .s360-viewer-overlay-close:hover { background: rgba(0,0,0,0.9); }
      .s360-viewer-overlay-nav {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        width: 36px;
        height: 36px;
        border-radius: 50%;
        border: none;
        background: rgba(0,0,0,0.5);
        color: #fff;
        font-size: 18px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 51;
        transition: background 0.2s;
      }
      .s360-viewer-overlay-nav:hover { background: rgba(0,0,0,0.8); }
      .s360-viewer-overlay-nav.prev { left: 10px; }
      .s360-viewer-overlay-nav.next { right: 10px; }

      /* Lightbox (full-screen) */
      .s360-lightbox {
        position: fixed;
        inset: 0;
        z-index: 10000;
        background: rgba(0,0,0,0.95);
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        transition: opacity 0.25s;
      }
      .s360-lightbox.visible { opacity: 1; }
      .s360-lightbox img {
        max-width: 90vw;
        max-height: 85vh;
        object-fit: contain;
        border-radius: 4px;
        user-select: none;
        border: none !important;
        box-shadow: none !important;
      }
      .s360-lightbox-close {
        position: absolute;
        top: 16px;
        right: 16px;
        width: 40px;
        height: 40px;
        border-radius: 50%;
        border: none;
        background: rgba(255,255,255,0.1);
        color: #fff;
        font-size: 20px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 0.2s;
      }
      .s360-lightbox-close:hover { background: rgba(255,255,255,0.25); }
      .s360-lightbox-nav {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        width: 48px;
        height: 48px;
        border-radius: 50%;
        border: none;
        background: rgba(255,255,255,0.1);
        color: #fff;
        font-size: 22px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 0.2s;
      }
      .s360-lightbox-nav:hover { background: rgba(255,255,255,0.25); }
      .s360-lightbox-nav.prev { left: 16px; }
      .s360-lightbox-nav.next { right: 16px; }
      .s360-lightbox-counter {
        position: absolute;
        bottom: 16px;
        left: 50%;
        transform: translateX(-50%);
        color: rgba(255,255,255,0.6);
        font-size: 13px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      }
    `;
    document.head.appendChild(style);
  }

  // ─── Build thumbnail ribbon ────────────────────────────

  private buildRibbon(): void {
    this.ribbon.className = 's360-gallery-ribbon';

    // "Back to 360°" thumbnail at the start — shows a boat frame with 360° icon
    const backBtn = document.createElement('div');
    backBtn.className = 's360-gallery-back-btn';
    backBtn.title = '360° View';

    if (this.config.spinThumbnailUrl) {
      const backImg = document.createElement('img');
      backImg.src = this.config.spinThumbnailUrl;
      backImg.alt = '360° View';
      backImg.setAttribute('data-no-fancybox', 'true');
      backImg.classList.add('s360-no-capture');
      backBtn.appendChild(backImg);
    }

    // 360° icon overlay
    const badge = document.createElement('div');
    badge.className = 's360-gallery-360-badge';
    badge.innerHTML = `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" fill="rgba(0,212,255,0.15)" stroke="#00d4ff" stroke-width="1.5"/>
      <path d="M12 6C8.69 6 6 8.69 6 12" stroke="#00d4ff" stroke-width="1.5" stroke-linecap="round"/>
      <path d="M12 6c3.31 0 6 2.69 6 6" stroke="#00d4ff" stroke-width="1.5" stroke-linecap="round" stroke-dasharray="2 2"/>
      <polygon points="5,10 7,12 5,14" fill="#00d4ff"/>
      <text x="12" y="15.5" text-anchor="middle" fill="white" font-size="5" font-weight="bold" font-family="Arial,sans-serif">360°</text>
    </svg>`;
    backBtn.appendChild(badge);

    backBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      this.deselectAll();
    });
    this.ribbon.appendChild(backBtn);

    // Thumbnails
    this.config.images.forEach((image, i) => {
      const thumb = document.createElement('div');
      thumb.className = 's360-gallery-thumb';
      thumb.dataset.index = String(i);

      const img = document.createElement('img');
      img.src = image.url;
      img.alt = image.alt || `Image ${i + 1}`;
      img.loading = 'lazy';
      // Prevent third-party lightbox scripts (Fancybox etc.) from picking up
      img.setAttribute('data-no-fancybox', 'true');
      img.setAttribute('data-see360-gallery', 'true');
      img.classList.add('s360-no-capture');

      thumb.appendChild(img);
      thumb.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        this.selectImage(i);
      });
      this.ribbon.appendChild(thumb);
    });

    // Build wrapper with scroll arrows
    const wrapper = document.createElement('div');
    wrapper.className = 's360-gallery-wrapper';

    // Left arrow
    const leftArrow = document.createElement('button');
    leftArrow.className = 's360-gallery-arrow s360-gallery-arrow--left';
    leftArrow.innerHTML = '<svg viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"/></svg>';
    leftArrow.addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      this.ribbon.scrollBy({ left: -200, behavior: 'smooth' });
    });

    // Right arrow
    const rightArrow = document.createElement('button');
    rightArrow.className = 's360-gallery-arrow s360-gallery-arrow--right';
    rightArrow.innerHTML = '<svg viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6"/></svg>';
    rightArrow.addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      this.ribbon.scrollBy({ left: 200, behavior: 'smooth' });
    });

    wrapper.appendChild(leftArrow);
    wrapper.appendChild(this.ribbon);
    wrapper.appendChild(rightArrow);

    // Append wrapper AFTER the viewer container (as a sibling, not inside)
    // This prevents dealer-site scripts from detecting images inside the viewer
    if (this.config.container.parentElement) {
      this.config.container.parentElement.insertBefore(
        wrapper,
        this.config.container.nextSibling
      );
    } else {
      // Fallback: append inside container
      this.config.container.appendChild(wrapper);
    }
  }

  // ─── Select an image (show in viewer area) ─────────────

  selectImage(index: number): void {
    if (index < 0 || index >= this.config.images.length) return;

    this.currentIndex = index;
    this.updateThumbnailHighlight();

    // Remove existing overlay
    if (this.viewerOverlay) {
      this.viewerOverlay.remove();
      this.viewerOverlay = null;
    }

    // Create overlay on top of viewer canvas
    const overlay = document.createElement('div');
    overlay.className = 's360-viewer-overlay';

    const img = document.createElement('img');
    img.src = this.config.images[index].url;
    img.alt = this.config.images[index].alt || `Image ${index + 1}`;

    // Click image → open lightbox
    img.addEventListener('click', () => this.openLightbox(index));

    // Close button → back to 360°
    const closeBtn = document.createElement('button');
    closeBtn.className = 's360-viewer-overlay-close';
    closeBtn.innerHTML = '✕';
    closeBtn.title = 'Back to 360° view';
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.deselectAll();
    });

    // Prev/Next nav arrows
    if (this.config.images.length > 1) {
      const prevBtn = document.createElement('button');
      prevBtn.className = 's360-viewer-overlay-nav prev';
      prevBtn.innerHTML = '‹';
      prevBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.selectImage((this.currentIndex - 1 + this.config.images.length) % this.config.images.length);
      });

      const nextBtn = document.createElement('button');
      nextBtn.className = 's360-viewer-overlay-nav next';
      nextBtn.innerHTML = '›';
      nextBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.selectImage((this.currentIndex + 1) % this.config.images.length);
      });

      overlay.appendChild(prevBtn);
      overlay.appendChild(nextBtn);
    }

    overlay.appendChild(closeBtn);
    overlay.appendChild(img);

    this.config.container.appendChild(overlay);
    this.viewerOverlay = overlay;

    this.config.onImageSelect?.(index);
  }

  // ─── Deselect → back to 360° ───────────────────────────

  deselectAll(): void {
    this.currentIndex = -1;
    if (this.viewerOverlay) {
      this.viewerOverlay.remove();
      this.viewerOverlay = null;
    }
    this.updateThumbnailHighlight();
  }

  // ─── Lightbox ──────────────────────────────────────────

  private openLightbox(index: number): void {
    this.currentIndex = index;

    if (this.lightbox) {
      this.updateLightbox();
      return;
    }

    const lb = document.createElement('div');
    lb.className = 's360-lightbox';

    const img = document.createElement('img');
    img.src = this.config.images[index].url;
    img.alt = this.config.images[index].alt || '';

    const closeBtn = document.createElement('button');
    closeBtn.className = 's360-lightbox-close';
    closeBtn.innerHTML = '✕';
    closeBtn.addEventListener('click', () => this.closeLightbox());

    const counter = document.createElement('div');
    counter.className = 's360-lightbox-counter';
    counter.textContent = `${index + 1} / ${this.config.images.length}`;

    // Nav
    if (this.config.images.length > 1) {
      const prevBtn = document.createElement('button');
      prevBtn.className = 's360-lightbox-nav prev';
      prevBtn.innerHTML = '‹';
      prevBtn.addEventListener('click', () => this.lightboxNav(-1));

      const nextBtn = document.createElement('button');
      nextBtn.className = 's360-lightbox-nav next';
      nextBtn.innerHTML = '›';
      nextBtn.addEventListener('click', () => this.lightboxNav(1));

      lb.appendChild(prevBtn);
      lb.appendChild(nextBtn);
    }

    lb.appendChild(closeBtn);
    lb.appendChild(img);
    lb.appendChild(counter);

    // Click backdrop to close
    lb.addEventListener('click', (e) => {
      if (e.target === lb) this.closeLightbox();
    });

    document.body.appendChild(lb);
    this.lightbox = lb;

    // Trigger transition
    requestAnimationFrame(() => lb.classList.add('visible'));

    // Touch swipe support
    this.addSwipeSupport(lb);
  }

  private updateLightbox(): void {
    if (!this.lightbox) return;
    const img = this.lightbox.querySelector('img');
    const counter = this.lightbox.querySelector('.s360-lightbox-counter');
    if (img) {
      img.src = this.config.images[this.currentIndex].url;
      img.alt = this.config.images[this.currentIndex].alt || '';
    }
    if (counter) {
      counter.textContent = `${this.currentIndex + 1} / ${this.config.images.length}`;
    }
  }

  private closeLightbox(): void {
    if (!this.lightbox) return;
    this.lightbox.classList.remove('visible');
    setTimeout(() => {
      this.lightbox?.remove();
      this.lightbox = null;
    }, 250);
  }

  private lightboxNav(direction: number): void {
    const newIndex = (this.currentIndex + direction + this.config.images.length) % this.config.images.length;
    this.currentIndex = newIndex;
    this.updateLightbox();

    // Also update the viewer overlay and thumbs
    this.selectImage(newIndex);
  }

  // ─── Touch swipe ───────────────────────────────────────

  private addSwipeSupport(element: HTMLElement): void {
    let startX = 0;
    let startY = 0;

    element.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    }, { passive: true });

    element.addEventListener('touchend', (e) => {
      const dx = e.changedTouches[0].clientX - startX;
      const dy = e.changedTouches[0].clientY - startY;

      // Must be mostly horizontal swipe
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        if (dx < 0) this.lightboxNav(1);  // swipe left = next
        else this.lightboxNav(-1);         // swipe right = prev
      }
    }, { passive: true });
  }

  // ─── Keyboard ──────────────────────────────────────────

  private bindKeys(): void {
    document.addEventListener('keydown', (e) => {
      if (!this.lightbox) return;

      if (e.key === 'Escape') {
        this.closeLightbox();
      } else if (e.key === 'ArrowLeft') {
        this.lightboxNav(-1);
      } else if (e.key === 'ArrowRight') {
        this.lightboxNav(1);
      }
    });
  }

  // ─── Helpers ───────────────────────────────────────────

  private updateThumbnailHighlight(): void {
    const thumbs = this.ribbon.querySelectorAll<HTMLDivElement>('.s360-gallery-thumb');
    thumbs.forEach((thumb, i) => {
      thumb.classList.toggle('active', i === this.currentIndex);
    });
  }

  /** Destroy the gallery (cleanup) */
  destroy(): void {
    this.ribbon.remove();
    this.viewerOverlay?.remove();
    this.closeLightbox();
  }

  /** Get current ribbon element */
  getElement(): HTMLDivElement {
    return this.ribbon;
  }
}
